import { z } from "zod";

/**
 * Wire shape for GET /api/me/v1/me/account-health.
 *
 * Mirrors backend pkg/api/identity.AccountHealthResponse 1:1.
 * Optional fields are omitempty on the server; the schema
 * uses .optional() rather than .nullable() to match.
 *
 * # Why the literals are pinned
 *
 * The four durability_status values and the per-tier strings
 * ("D3" / "D2" / "D1") are the §4.6 canonical vocabulary. The
 * methodfloor package defines them on the Go side; the
 * design-system component defines them on the TypeScript
 * side. If a future backend bump renames one of these (e.g.,
 * "fragile" → "critical"), the Zod parse rejects the response
 * loudly here rather than silently mis-rendering downstream.
 */

export const durabilityStatusSchema = z.enum([
  "strong",
  "standard",
  "at_risk",
  "fragile",
]);
export type DurabilityStatus = z.infer<typeof durabilityStatusSchema>;

export const methodKindSchema = z.enum([
  "passkey",
  "totp",
  "recovery_email",
  "recovery_code",
]);
export type MethodKind = z.infer<typeof methodKindSchema>;

export const accountHealthTierCountsSchema = z.object({
  d3: z.number().int().nonnegative(),
  d2: z.number().int().nonnegative(),
  d1: z.number().int().nonnegative(),
});
export type AccountHealthTierCounts = z.infer<typeof accountHealthTierCountsSchema>;

export const accountHealthMethodSchema = z.object({
  kind: methodKindSchema,
  tier: z.string(), // "D3" | "D2" | "D1" but unconstrained to absorb future tier additions
  id: z.string().optional(),
  friendly_name: z.string().optional(),
  authenticator_name: z.string().optional(),
  added_at: z.string().optional(),
  last_used_at: z.string().optional(),
});
export type AccountHealthMethod = z.infer<typeof accountHealthMethodSchema>;

/**
 * The tenant's TIER floor, or "" for no tier floor at all.
 *
 * NOT durabilityStatusSchema, and the difference is the bug that reached a user on 2026-07-17: a brand
 * new tenant's account-health page rendered "Something went wrong", because this field reused the strict
 * four-value enum for something that is ABSENT by default.
 *
 * The two look identical and mean different things:
 *
 *   durability_status          — the USER's state. Always one of the four. Strict is correct.
 *   tenant_policy.min_durability — a THRESHOLD. NULL by default (000040:113): the tier leg is a
 *                                 per-tenant opt-in, so "no tier floor" is the SHIPPED posture, not an
 *                                 edge case. Go serialises NULL as "" (no omitempty on the DTO), and ""
 *                                 is not a durability status. It is the absence of one.
 *
 * So the strict enum rejected the default configuration of every tenant, and the strict-enum RATIONALE
 * above ("if a future backend renames one of these, fail loudly rather than mis-render") was sound for
 * the field it was written about and inherited by a field it did not fit.
 *
 * "" is not tolerance for junk: it is a specific, documented value with a meaning. An unknown STATUS
 * still fails loudly, which is what that rationale actually wanted.
 */
export const durabilityFloorSchema = z.union([durabilityStatusSchema, z.literal("")]);
export type DurabilityFloor = z.infer<typeof durabilityFloorSchema>;

export const accountHealthTenantPolicySchema = z.object({
  min_durability: durabilityFloorSchema,
  min_total: z.number().int().nonnegative(),
  allow_totp: z.boolean(),
  allow_recovery_email: z.boolean(),
  allow_recovery_codes: z.boolean(),
  // "off" | "observe" | "enforce". Not an enum on purpose: an unknown mode from a newer backend must
  // not fail-parse the whole account-health read and blank the portal's safety surfaces. The portal
  // does not gate anything on this today; it is here so the shape is honest.
  enforcement_mode: z.string().min(1).max(16),
});
export type AccountHealthTenantPolicy = z.infer<typeof accountHealthTenantPolicySchema>;

export const accountHealthResponseSchema = z.object({
  durability_status: durabilityStatusSchema,
  tier_counts: accountHealthTierCountsSchema,
  methods: z.array(accountHealthMethodSchema),
  tenant_policy: accountHealthTenantPolicySchema,
});
export type AccountHealthResponse = z.infer<typeof accountHealthResponseSchema>;
